import type { TOC } from '@ember/component/template-only';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import { eq } from 'ember-truth-helpers';
import UiCard from 'garaje/components/ui/card';
import UiPaginationCombined from 'garaje/components/ui-pagination/combined';

interface TimelineEvent {
  processedDateDisplay: string;
  processedTimeDisplay: string;
  integrationName: string;
  locationName: string | null;
  operationType: string;
  operationTypeLabel: string;
  operationDefinition: string | null;
  operationStatus: string;
  failureType: string | null;
  durationMillis: number;
  primaryMetric: string | null;
}

interface HealthDashboardEventsTableSignature {
  Args: {
    filteredEvents: TimelineEvent[];
    paginatedEvents: TimelineEvent[];
    openDrawer: (event: TimelineEvent) => void;
    pageCount: number;
    page: number;
    perPage: number;
    recordCount: number;
    setPage: (page: number) => void;
  };
}

export default <template>
  {{#if @filteredEvents.length}}
    <table class="table w-full">
      <thead>
        <tr
          class="table-row grid grid-cols-12 gap-4 py-2 border-b border-t text-carbon-50 font-bold text-xs tracking-wide items-center"
        >
          <th class="table-cell col-span-2 text-left">Processed Time</th>
          <th class="table-cell col-span-1 text-center">Integration</th>
          <th class="table-cell col-span-2 text-center">Location Name</th>
          <th class="table-cell col-span-2 text-center">Operation Type</th>
          <th class="table-cell col-span-1 text-center">Outcome</th>
          <th class="table-cell col-span-1 text-center">Duration</th>
          <th class="table-cell col-span-2 text-center">Primary Metric</th>
          <th class="table-cell col-span-1 text-left">Details</th>
        </tr>
      </thead>

      <tbody>
        {{#each @paginatedEvents as |event|}}
          <tr
            class="table-row grid grid-cols-12 gap-4 py-3 border-b text-sm items-center hover:bg-carbon-5
              {{if (eq event.operationStatus 'OK') '' 'hover:cursor-pointer'}}"
            {{on "click" (fn @openDrawer event)}}
          >
            <td class="table-cell col-span-2 text-xs">
              <div class="flex flex-col">
                <span class="font-medium text-carbon-70">{{event.processedDateDisplay}}</span>
                <span class="text-carbon-50">{{event.processedTimeDisplay}}</span>
              </div>
            </td>

            <td class="table-cell col-span-1 font-medium text-xs min-w-0 truncate text-center">
              {{event.integrationName}}
              <EmberTooltip @side="top" @text={{event.integrationName}} />
            </td>

            <td class="table-cell col-span-2 text-xs min-w-0 truncate text-center">
              {{#if event.locationName}}
                <span class="text-carbon-70">{{event.locationName}}</span>
                <EmberTooltip @side="top" @text={{event.locationName}} />
              {{else}}
                <span class="text-carbon-30">–</span>
              {{/if}}
            </td>

            <td class="table-cell col-span-2 text-xs font-medium min-w-0 truncate text-center">
              {{#if (eq event.operationType "POLL")}}
                <span class="text-blue-600">Poll</span>
              {{else if (eq event.operationType "WEBHOOK")}}
                <span class="text-purple-600">Webhook</span>
              {{else if (eq event.operationType "USER_LOOKUP")}}
                <span class="text-teal-600">User Lookup</span>
              {{else if (eq event.operationType "CREDENTIAL_CREATE")}}
                <span class="text-orange-600">Credential Create</span>
              {{else if (eq event.operationType "AUTH")}}
                <span class="text-sky-600">Auth</span>
              {{else if (eq event.operationType "VALIDATE")}}
                <span class="text-indigo-600">Validate</span>
              {{else}}
                <span class="text-carbon-70">{{event.operationTypeLabel}}</span>
              {{/if}}
              {{#if event.operationDefinition}}
                <EmberTooltip @side="top" @text={{event.operationDefinition}} />
              {{/if}}
            </td>

            <td class="table-cell col-span-1 text-xs text-center">
              {{#if (eq event.operationStatus "OK")}}
                <span class="flex items-center gap-1 text-green-700 font-medium">
                  <div class="shrink-0 w-3.5 h-3.5">{{svgJar "checkmark-circle-green" height="14" width="14"}}</div>
                  Success
                </span>
              {{else if (eq event.operationStatus "FAILED")}}
                {{#if (eq event.failureType "TIMEOUT")}}
                  <span class="flex items-center gap-1 text-yellow-600 font-medium">
                    <div class="shrink-0 w-3.5 h-3.5">{{svgJar "caution-triangle" height="14" width="14"}}</div>
                    Timeout
                  </span>
                {{else}}
                  <span class="flex items-center gap-1 text-red-600 font-medium">
                    <div class="shrink-0 w-3.5 h-3.5">{{svgJar "circle-cross-red" height="14" width="14"}}</div>
                    Failed
                  </span>
                {{/if}}
              {{else if (eq event.operationStatus "PARTIAL")}}
                <span class="flex items-center gap-1 text-yellow-600 font-medium">
                  <div class="shrink-0 w-3.5 h-3.5">{{svgJar "caution-triangle" height="14" width="14"}}</div>
                  Partial
                </span>
              {{/if}}
            </td>

            <td class="table-cell col-span-1 text-carbon-70 text-xs text-center">
              {{event.durationMillis}}ms
            </td>

            <td class="table-cell col-span-2 text-xs min-w-0 truncate text-center">
              {{#if event.primaryMetric}}
                <span class="text-carbon-70">{{event.primaryMetric}}</span>
                <EmberTooltip @side="top" @text={{event.primaryMetric}} />
              {{else}}
                <span class="text-carbon-30">–</span>
              {{/if}}
            </td>

            <td class="table-cell col-span-1 text-xs">
              {{#if (eq event.operationStatus "OK")}}
                <div class="inline-flex cursor-not-allowed text-carbon-20">
                  <div class="shrink-0 w-3.5 h-3.5">{{svgJar "chevron-grey-right" height="14" width="14"}}</div>
                  <EmberTooltip @side="top" @text="This event has no errors" />
                </div>
              {{else}}
                <button
                  type="button"
                  class="flex items-center gap-1 text-carbon-50 hover:text-carbon-70"
                  {{on "click" (fn @openDrawer event)}}
                >
                  <div class="shrink-0 w-3.5 h-3.5">{{svgJar "chevron-grey-right" height="14" width="14"}}</div>
                </button>
              {{/if}}
            </td>
          </tr>
        {{/each}}
      </tbody>
    </table>

    <UiPaginationCombined
      @pageCount={{@pageCount}}
      @pageNumber={{@page}}
      @pageSize={{@perPage}}
      @recordCount={{@recordCount}}
      @setPageNumber={{@setPage}}
    />

  {{else}}
    <UiCard class="flex flex-col items-center justify-center py-24 text-center border-dashed">
      <div class="text-carbon-50 text-lg font-medium mb-2">No events found</div>
      <div class="text-carbon-30 text-sm">Try adjusting your filters or syncing data.</div>
    </UiCard>
  {{/if}}
</template> satisfies TOC<HealthDashboardEventsTableSignature>;
